import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  getUser,
  selectAuthUser,
  selectIsAuthenticated,
  setPermissions,
} from '@store/reducers/authSlice';
import { useLazyAuthControllerUserQuery } from '@store/services/authApi';
import { useLazyUsersControllerPermissionsQuery } from '@store/services/usersApi';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useLocation, useNavigate } from 'react-router';

import { closeConfirmPrompt, openConfirmPrompt } from '../Prompt/globalConfirmPromptSlice';

export const publicPaths = [
  '/',
  '/login',
  '/register',
  '/question',
  '/permissionDenied',
  '/notFound',
  '/article',
];

const useNavigateGuard = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const confirmPromptState = useAppSelector(state => state.globalConfirmPrompt);
  const storedUser = useAppSelector(selectAuthUser);

  const lastSafeLocationRef = useRef<string>('/');

  const [fetchUser, { data: userData, isFetching: fetchingUser }] =
    useLazyAuthControllerUserQuery();
  const [fetchPermissions, { data: permissions, isFetching: fetchingPermissions }] =
    useLazyUsersControllerPermissionsQuery();

  const nonAuthenticated = useCallback(() => {
    const path = window.location.pathname.split('/');
    const currentPath = `/${path[1]}`;
    return publicPaths.includes(currentPath) || publicPaths.includes(window.location.pathname);
  }, [window.location.pathname]);

  const isPublicPath = useCallback((pathname: string): boolean => {
    const path = pathname.split('/');
    const currentPath = `/${path[1]}`;
    return publicPaths.includes(currentPath) || publicPaths.includes(pathname);
  }, []);

  const shouldBlockNavigation = useCallback(
    ({ nextLocation }: { nextLocation: { pathname: string } }): boolean => {
      // Don't block if user is authenticated
      if (isAuthenticated) {
        return false;
      }

      // Don't block if navigating to public paths
      if (isPublicPath(nextLocation.pathname)) {
        return false;
      }

      // Don't block if confirm prompt is already open (to avoid infinite loops)
      if (confirmPromptState.isOpen) {
        return false;
      }

      return true;
    },
    [isAuthenticated, isPublicPath, confirmPromptState.isOpen],
  );

  const blocker = useBlocker(shouldBlockNavigation);

  const requireLogin = useCallback(
    (options: { redirectPath?: string; fallbackPath?: string } = {}) => {
      dispatch(
        openConfirmPrompt({
          title: t('tips.require-login'),
          message: t('tips.require-login-message'),
          module: 'login',
          redirectPath: options.redirectPath ?? location.pathname,
          fallbackPath: options.fallbackPath ?? '/',
          buttons: {
            confirm: t('controls.login'),
            cancel: t('controls.cancel'),
          },
        }),
      );
    },
    [dispatch, t, location.pathname],
  );

  // Handle blocked navigation
  useEffect(() => {
    if (!nonAuthenticated() && !isAuthenticated && blocker.state !== 'blocked') {
      // Use replace: true to avoid adding unnecessary entries to history
      navigate(lastSafeLocationRef.current, { replace: true });

      // Open login confirmation prompt with the intended destination
      requireLogin({
        redirectPath: location.pathname,
        fallbackPath: lastSafeLocationRef.current,
      });
    } else if (blocker.state === 'blocked') {
      // Store the current safe location
      lastSafeLocationRef.current = location.pathname;

      // Open login confirmation prompt with the intended destination
      requireLogin({
        redirectPath: blocker.location?.pathname,
        fallbackPath: lastSafeLocationRef.current,
      });
    }
  }, [blocker.state, blocker.location?.pathname, dispatch, location.pathname]);

  useEffect(() => {
    // Only fetch user data if we're authenticated but don't have user data yet
    if (isAuthenticated && !storedUser && !fetchingUser) {
      (async () => {
        try {
          const user = await fetchUser().unwrap();
          dispatch(getUser(user));
          i18n.changeLanguage(user.defaultLanguage ?? 'cnt');

          const permissions = await fetchPermissions().unwrap();
          dispatch(setPermissions(permissions));
        } catch (_e) {
          // If user fetch fails, show login confirmation instead of direct navigation
          // dispatch(logout());
          // requireLogin();
        }
      })();
    }
  }, [isAuthenticated, storedUser, fetchingUser, fetchUser, dispatch, i18n, requireLogin]);

  useEffect(() => {
    // Check authentication status when location changes
    // Only show login confirmation if all conditions are met:
    // 1. User is not authenticated
    // 2. We're on a protected route
    // 3. Navigation isn't already blocked
    // 4. We're not currently fetching user data
    // 5. No prompt is already open
    // 6. We're not in the middle of a login redirect flow
    const timeoutId = setTimeout(() => {
      const isInRedirectFlow = sessionStorage.getItem('redirectAfterLogin') !== null;

      if (
        !isAuthenticated &&
        !nonAuthenticated() &&
        blocker.state !== 'blocked' &&
        !fetchingUser &&
        !confirmPromptState.isOpen &&
        !isInRedirectFlow
      ) {
        console.log('User is not authenticated, showing login confirmation');
        requireLogin();
      }
    }, 150); // Slightly longer delay to ensure state is updated

    return () => clearTimeout(timeoutId);
  }, [
    isAuthenticated,
    nonAuthenticated,
    requireLogin,
    blocker.state,
    fetchingUser,
    confirmPromptState.isOpen,
    location.pathname, // Add location to dependencies
  ]);

  // Close any login prompts when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && confirmPromptState.isOpen && confirmPromptState.module === 'login') {
      dispatch(closeConfirmPrompt());
    }
  }, [isAuthenticated, confirmPromptState.isOpen, confirmPromptState.module, dispatch]);

  return {
    userData,
    fetchingUser,
    permissions,
    fetchingPermissions,
    requireLogin,
  };
};

export default useNavigateGuard;
